// AchievementSystem.tsx - Progress and achievement tracking
// Users should feel a sense of progress and mastery as they use Cipher

import { useState, useEffect, useCallback } from 'react';
import {
  Star, Bell, Eye, Search, Briefcase, BarChart3, Zap,
  Shield, Newspaper, Users, Award, Flame, Target, Trophy
} from 'lucide-react';
import { loadProfile, earnAchievement, hasAchievement } from '../../services/personalizationService';
import './AchievementSystem.css';

// Achievement definitions
export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  tier: 'first-steps' | 'analyst' | 'expert';
  hint: string; // Hint for unearned achievements
}

export const ACHIEVEMENTS: Achievement[] = [
  // First Steps tier
  {
    id: 'first-watch',
    name: 'First Watch',
    description: 'Added first ticker to watchlist',
    icon: <Star size={20} />,
    tier: 'first-steps',
    hint: 'Add a stock to your watchlist',
  },
  {
    id: 'first-alert',
    name: 'First Alert',
    description: 'Created first price alert',
    icon: <Bell size={20} />,
    tier: 'first-steps',
    hint: 'Set up a price alert',
  },
  {
    id: 'market-watcher',
    name: 'Market Watcher',
    description: 'Visited app 3 days in a row',
    icon: <Eye size={20} />,
    tier: 'first-steps',
    hint: 'Visit Cipher for 3 consecutive days',
  },
  {
    id: 'researcher',
    name: 'Researcher',
    description: 'Viewed 10 different stock detail pages',
    icon: <Search size={20} />,
    tier: 'first-steps',
    hint: 'Research 10 different stocks',
  },
  {
    id: 'portfolio-starter',
    name: 'Portfolio Starter',
    description: 'Added first portfolio position',
    icon: <Briefcase size={20} />,
    tier: 'first-steps',
    hint: 'Add a position to your portfolio',
  },

  // Analyst tier
  {
    id: 'screener-pro',
    name: 'Screener Pro',
    description: 'Ran 5 screener searches',
    icon: <BarChart3 size={20} />,
    tier: 'analyst',
    hint: 'Run 5 stock screens',
  },
  {
    id: 'alert-master',
    name: 'Alert Master',
    description: 'Created 10 active alerts',
    icon: <Bell size={20} />,
    tier: 'analyst',
    hint: 'Set up 10 alerts',
  },
  {
    id: 'diversified',
    name: 'Diversified',
    description: 'Portfolio has stocks in 3+ sectors',
    icon: <Shield size={20} />,
    tier: 'analyst',
    hint: 'Add stocks from 3 different sectors',
  },
  {
    id: 'strategy-tester',
    name: 'Strategy Tester',
    description: 'Ran first backtest',
    icon: <Zap size={20} />,
    tier: 'analyst',
    hint: 'Run a strategy backtest',
  },
  {
    id: 'news-reader',
    name: 'News Reader',
    description: 'Viewed 20 news impact articles',
    icon: <Newspaper size={20} />,
    tier: 'analyst',
    hint: 'Read 20 news articles',
  },

  // Expert tier
  {
    id: 'apex-expert',
    name: 'Cipher Expert',
    description: 'Viewed 50 different stocks',
    icon: <Award size={20} />,
    tier: 'expert',
    hint: 'Research 50 different stocks',
  },
  {
    id: 'strategy-developer',
    name: 'Strategy Developer',
    description: 'Created and saved a custom strategy',
    icon: <Target size={20} />,
    tier: 'expert',
    hint: 'Save a custom trading strategy',
  },
  {
    id: 'consistent-investor',
    name: 'Consistent Investor',
    description: '30-day streak',
    icon: <Flame size={20} />,
    tier: 'expert',
    hint: 'Maintain a 30-day login streak',
  },
  {
    id: 'community-contributor',
    name: 'Community Contributor',
    description: 'Published a public strategy',
    icon: <Users size={20} />,
    tier: 'expert',
    hint: 'Share a strategy with the community',
  },
  {
    id: 'power-user',
    name: 'Power User',
    description: 'Used 10 different Cipher features',
    icon: <Trophy size={20} />,
    tier: 'expert',
    hint: 'Explore all major Cipher features',
  },
];

// Achievement notification toast
interface AchievementToastProps {
  achievement: Achievement;
  onClose: () => void;
}

export function AchievementToast({ achievement, onClose }: AchievementToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="achievement-toast" onClick={onClose}>
      <div className={`achievement-toast-icon ${achievement.tier}`}>
        {achievement.icon}
      </div>
      <div className="achievement-toast-content">
        <span className="achievement-toast-label">Achievement Unlocked</span>
        <span className="achievement-toast-name">{achievement.name}</span>
        <span className="achievement-toast-desc">{achievement.description}</span>
      </div>
    </div>
  );
}

// Achievement progress panel (for Settings page)
export function AchievementProgress() {
  const profile = loadProfile();
  const earned = profile.achievements_earned || [];

  const tierCounts = {
    'first-steps': ACHIEVEMENTS.filter(a => a.tier === 'first-steps').length,
    'analyst': ACHIEVEMENTS.filter(a => a.tier === 'analyst').length,
    'expert': ACHIEVEMENTS.filter(a => a.tier === 'expert').length,
  };

  const earnedCounts = {
    'first-steps': ACHIEVEMENTS.filter(a => a.tier === 'first-steps' && earned.includes(a.id)).length,
    'analyst': ACHIEVEMENTS.filter(a => a.tier === 'analyst' && earned.includes(a.id)).length,
    'expert': ACHIEVEMENTS.filter(a => a.tier === 'expert' && earned.includes(a.id)).length,
  };

  return (
    <div className="achievement-progress">
      <div className="achievement-progress-header">
        <Trophy size={24} />
        <div>
          <h3>My Progress</h3>
          <p>{earned.length} of {ACHIEVEMENTS.length} achievements earned</p>
        </div>
      </div>

      <div className="achievement-tiers">
        {(['first-steps', 'analyst', 'expert'] as const).map(tier => (
          <div key={tier} className="achievement-tier">
            <div className="tier-header">
              <span className="tier-name">
                {tier === 'first-steps' ? 'First Steps' : tier === 'analyst' ? 'Analyst' : 'Expert'}
              </span>
              <span className="tier-progress">
                {earnedCounts[tier]} / {tierCounts[tier]}
              </span>
            </div>

            <div className="achievement-grid">
              {ACHIEVEMENTS.filter(a => a.tier === tier).map(achievement => {
                const isEarned = earned.includes(achievement.id);
                return (
                  <div
                    key={achievement.id}
                    className={`achievement-badge ${isEarned ? 'earned' : 'locked'}`}
                    title={isEarned ? achievement.description : achievement.hint}
                  >
                    <div className={`badge-icon ${tier}`}>
                      {achievement.icon}
                    </div>
                    <span className="badge-name">{achievement.name}</span>
                    {!isEarned && (
                      <span className="badge-hint">{achievement.hint}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Achievement manager hook - checks conditions and awards achievements
export function useAchievementManager() {
  const [toast, setToast] = useState<Achievement | null>(null);

  const checkAndAward = useCallback((achievementId: string) => {
    const achievement = ACHIEVEMENTS.find(a => a.id === achievementId);
    if (!achievement) return false;

    if (!hasAchievement(achievementId)) {
      const isNew = earnAchievement(achievementId);
      if (isNew) {
        setToast(achievement);
        return true;
      }
    }
    return false;
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  // Auto-check streak-based achievements
  useEffect(() => {
    const profile = loadProfile();

    // Market Watcher - 3 day streak
    if (profile.streak_days >= 3) {
      checkAndAward('market-watcher');
    }

    // Consistent Investor - 30 day streak
    if (profile.streak_days >= 30) {
      checkAndAward('consistent-investor');
    }

    // Researcher - 10 stocks viewed
    if (profile.most_viewed_tickers.length >= 10) {
      checkAndAward('researcher');
    }

    // Cipher Expert - 50 stocks viewed
    if (profile.most_viewed_tickers.length >= 50) {
      checkAndAward('apex-expert');
    }

    // Power User - 10 features used
    if (profile.most_used_features.length >= 10) {
      checkAndAward('power-user');
    }
  }, [checkAndAward]);

  return {
    toast,
    dismissToast,
    checkAndAward,
  };
}

// Toast container component
interface AchievementToastContainerProps {
  toast: Achievement | null;
  onDismiss: () => void;
}

export function AchievementToastContainer({ toast, onDismiss }: AchievementToastContainerProps) {
  if (!toast) return null;

  return (
    <div className="achievement-toast-container">
      <AchievementToast achievement={toast} onClose={onDismiss} />
    </div>
  );
}

export default {
  ACHIEVEMENTS,
  AchievementToast,
  AchievementProgress,
  AchievementToastContainer,
  useAchievementManager,
};
